import { Component, computed, signal } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { MatDialogClose } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import {
    selectOrganizationName, selectProfileBusy,
    selectProfileFullName,
    selectProfileGeneralTermsUrl,
    selectProfileIdentityName,
    selectProfileImpersonationMenu, selectProfileImpersonator, selectProfileLogins, selectProfileLogo,
    selectProfilePrivacyPolicyUrl, selectProfileSquareLogo,
    selectProfileUsername, selectUserAvatar, selectUserEmail
} from '../../../store/profile/profile.state';
import { RouterLink } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { profileReload, profileImpersonate, profileEndImpersonation, profileSaveAvatar } from '../../../store/profile/profile.actions';
import { LoginProvider } from '../../../enum/login-provider.enum';
import { MatMenuModule } from '@angular/material/menu';
import { ImageCropperModule } from 'ngx-image-cropper';
import { MatSnackBar } from '@angular/material/snack-bar';
import { addPasskey, addSocialLink, logout, removeSocialLink } from '../../../store/login/login.actions';
import { IconEnum } from '../../../enum/icon.enum';

@Component({
    selector: 'app-profile-modal',
    imports: [
        IconComponent,
        MatDialogClose,
        MatTooltip,
        MatButton,
        MatIconButton,
        MatMenuModule,
        ImageCropperModule
    ],
    templateUrl: './profile-modal.component.html',
    styleUrl: './profile-modal.component.scss'
})
export class ProfileModalComponent {

    public uploadingAvatar = signal<boolean>(false);
    public fileChangeEvent: Event = null;
    public avatarImage: string = null;

    constructor(private store: Store<AppState>, private snackBar: MatSnackBar) {}

    public username = this.store.selectSignal(selectProfileUsername);
    public email = this.store.selectSignal(selectUserEmail);
    public fullName = this.store.selectSignal(selectProfileFullName);
    public organizationName = this.store.selectSignal(selectOrganizationName);
    public identityName = this.store.selectSignal(selectProfileIdentityName);
    public avatarUrl = this.store.selectSignal(selectUserAvatar);
    public generalTermsUrl = this.store.selectSignal(selectProfileGeneralTermsUrl);
    public privacyPolicyUrl = this.store.selectSignal(selectProfilePrivacyPolicyUrl);
    public identityLogoSquare = this.store.selectSignal(selectProfileSquareLogo);
    public identityLogoRegular = this.store.selectSignal(selectProfileLogo);
    public profileLogins = this.store.selectSignal(selectProfileLogins);
    public profileBusy = this.store.selectSignal(selectProfileBusy);
    public profileImpersonator = this.store.selectSignal(selectProfileImpersonator);
    public profileImpersonationMenu = this.store.selectSignal(selectProfileImpersonationMenu);

    public isImpersonating = computed(() => this.profileImpersonator() !== null);
    public showImpersonationMenu = computed(() =>
        !this.isImpersonating() && this.profileImpersonationMenu()?.length > 0
    );
    public displayUsername = computed(() => '@' + this.username());
    public displayImpersonator = computed(() => '@' + this.profileImpersonator());

    public identityLogo = computed(() => {
        const output = {
            logoUrl: this.identityLogoSquare(),
            identityName: this.identityName(),
        };
        if (output.logoUrl === null) {
            output.logoUrl = this.identityLogoRegular();
            output.identityName = null;
        }
        return output;
    });

    public activeSocialLogins = computed(() => {
        return this.profileLogins()
            .filter(login => login.active)
            .map(login => ({
                id: login.key,
                icon: this.socialIcon(login.type),
                label: login.label,
                email: login.email,
                editable: login.editable
            }));
    });

    public inactiveSocialLogins = computed(() => {
        return this.profileLogins()
            .filter(login => !login.active && login.editable)
            .map(login => ({
                id: login.key,
                icon: this.socialIcon(login.type),
                label: login.label,
                email: login.email
            }));
    });

    public showSocialLoginButton = computed(() => {
        return this.inactiveSocialLogins().length > 0;
    });

    public changeProfilePhoto(): void {

        console.log('changeProfilePhoto');
    }

    public addPassKey(): void {
        this.store.dispatch(addPasskey());
    }

    public logout(): void {
        this.store.dispatch(logout());
    }

    public connectSocialLogin(provider: string): void {
        this.store.dispatch(addSocialLink({ provider }));
    }

    public disconnectSocialLogin(provider: string): void {
        this.store.dispatch(removeSocialLink({ provider }));
    }

    public profileRefresh(): void {
        this.store.dispatch(profileReload());
    }

    public profileImpersonate(username: string): void {
        this.store.dispatch(profileImpersonate({ username }));
    }

    public profileEndImpersonation(): void {
        this.store.dispatch(profileEndImpersonation());
    }

    public onFileSelected(event: any): void {
        const target = event.target as HTMLInputElement;
        const file = target.files[0];
        if (file) {
            const fileType = file.type;
            if (fileType === 'image/png' || fileType === 'image/jpeg' || fileType === 'image/jpg') {
                this.uploadingAvatar.set(true);
                this.fileChangeEvent = event;
            } else {
                this.snackBar.open('Invalid file type. Please select a PNG, JPEG, or JPG image.', 'Close', {
                    duration: 3000,
                });
            }
        }
    }

    public imageCropped(event: any): void {
        this.avatarImage = event.base64;
        console.log(this.avatarImage);
    }

    public cancelAvatarUpload(): void {
        this.uploadingAvatar.set(false);
        this.fileChangeEvent = null;
    }

    public saveAvatarUpload(): void {
        this.uploadingAvatar.set(false);
        this.fileChangeEvent = null;
        this.store.dispatch(profileSaveAvatar({ userAvatar: this.avatarImage }));
    }

    private socialIcon(loginType: string): IconEnum {
        switch (loginType) {
            case LoginProvider.GOOGLE:
                return 'google';
            case LoginProvider.AMAZON:
                return 'amazon';
            case LoginProvider.FACEBOOK:
                return 'facebook';
            case LoginProvider.APPLE:
                return 'apple';
            default:
                return 'grid-2';
        }
    }
}
